/**
 * The plan and the list (#1283).
 *
 * Two tabs over one object, because they are two journeys over the same data
 * and neither is a subset of the other. THE PLAN is read at home, in day order.
 * THE LIST is read in a shop, in aisle order, one thumb, bad signal.
 *
 * Three things that matter more than they look:
 *
 *   AN UNSOLVED LUNCH SAYS SO. A slot the engine could not fill is a real
 *   state. Filling it with something nobody will eat is how a plan stops being
 *   believed, and once it is not believed it is not used.
 *
 *   TICKING IS OPTIMISTIC. In a shop the request may not land for seconds. The
 *   line has to strike through under the thumb, and roll back honestly if the
 *   write actually failed.
 *
 *   A LINE SAYS WHY IT IS THERE. "Heavy cream — Cajun Pasta, Pot Pie" is one
 *   line with two reasons. Two lines is what nobody reconciles in the aisle.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  twoPlates,
  type Eater,
  AISLE_ORDER,
  AISLE_LABEL,
  DAY_LABELS,
  type Plan,
  type PlanItem,
  type ItemPreference,
  type PlanSlot,
} from "../api/twoPlates";
import { ApiError } from "../lib/api";
import { ShareList } from "../components/ShareList";
import { HowWasIt } from "../components/HowWasIt";
import { DidItWork } from "../components/DidItWork";
import { SwapMeal } from "../components/SwapMeal";

const SLOT_ORDER: PlanSlot["slot"][] = ["breakfast", "lunch", "dinner"];

export default function PlanPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"plan" | "list">("plan");

  const requested = useMemo(
    () =>
      (params.get("meals") ?? "")
        .split(",")
        .map((n) => Number(n))
        .filter((n) => Number.isInteger(n) && n > 0),
    [params],
  );

  const current = useQuery({ queryKey: ["plan"], queryFn: twoPlates.currentPlan });

  const create = useMutation({
    mutationFn: () => twoPlates.createPlan({ mealIds: requested }),
    onSuccess: (res) => queryClient.setQueryData(["plan"], { plan: res.plan }),
  });

  const plan = create.data?.plan ?? current.data?.plan ?? null;

  /**
   * Is the plan on screen last week's?
   *
   * ⛔ Only when it came from the server. A plan built moments ago in `create`
   * is this week's by definition, and reading a stale flag that belongs to the
   * PREVIOUS response would tell somebody their brand-new week was over.
   */
  const staleWeek = !create.data && !!current.data?.stale;

  // A ?meals= in the URL is a hand-off from the deck. Build ONCE.
  //
  // ⛔ It builds even when a plan ALREADY EXISTS. This used to bail on any
  // truthy `plan`, which meant the very first plan a household ever made was
  // the only one it could make: every deck session after it handed off three
  // chosen meals, found the old plan, and silently showed that instead. Three
  // nights picked and nothing to show for it, with no error to explain why.
  // `/plans/current` is simply the most recent plan, so building supersedes the
  // old one without destroying it.
  //
  // The ref is not belt-and-braces: StrictMode runs effects twice in
  // development, and without it the first thing a developer sees is two
  // identical plans and a shopping list bought twice.
  const built = useRef(false);
  useEffect(() => {
    if (built.current) return;
    if (current.isLoading) return;
    if (requested.length === 0) return;
    built.current = true;
    create.mutate(undefined, {
      // Spend the hand-off once it has been honoured. Left in the URL, a
      // refresh or a back-button would build the same three nights again and
      // shop for them twice.
      onSuccess: () => navigate("/plan", { replace: true }),
    });
    // The deck hands off once; nothing here should re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.isLoading, requested.length]);

  if (current.isLoading || create.isPending) {
    return <Shell><p className="text-muted text-sm">Building the plan…</p></Shell>;
  }

  if (create.isError) {
    return (
      <Shell>
        <p className="text-warn text-sm">
          {create.error instanceof ApiError ? create.error.message : "Could not build the plan"}
        </p>
        <button
          onClick={() => navigate("/deck")}
          className="text-muted mt-4 text-sm underline underline-offset-4"
        >
          Back to the deck
        </button>
      </Shell>
    );
  }

  if (!plan) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold tracking-tight">No plan yet.</h1>
        <p className="text-muted mt-2 text-sm">Pick your nights and this fills itself in.</p>
        <button
          onClick={() => navigate("/deck")}
          className="bg-plate-b text-ground tap mt-6 w-full rounded-lg font-semibold"
        >
          Open the deck
        </button>
      </Shell>
    );
  }

  const remaining = plan.items.filter((i) => !i.checked && !i.haveAlready).length;
  const dayCount = new Set(plan.slots.map((s) => s.dayIndex)).size;

  return (
    <Shell>
      <header className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{dayCount} days</h1>
          <p className="text-muted mt-1 text-xs">
            Shopping {new Date(plan.shopDate).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            {" · "}
            {remaining} {remaining === 1 ? "thing" : "things"} to buy
          </p>
        </div>
        {/**
         * ⛔ THERE WAS NO WAY TO START THE NEXT WEEK. A plan is only ever built
         * from a `?meals=` hand-off out of the deck, so the only route to a new
         * week was guessing you had to go back and swipe — nothing on this
         * screen said so, and the screen you are looking at is last week's.
         *
         * ⚠️ It goes to the DECK rather than building immediately, because a
         * week is made of chosen meals and a button that skipped the choosing
         * would have to invent them. Building supersedes the old plan without
         * destroying it, so nothing is lost by going to look.
         */}
        <Link
          to="/deck"
          className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold print:hidden ${
            staleWeek ? "bg-plate-b text-ground" : "border-line text-muted border"
          }`}
        >
          {staleWeek ? "Plan this week" : "Plan next week"}
        </Link>
      </header>

      {/* ⛔ SAYS IT IS OVER; DOES NOT TAKE IT AWAY. The week turns on the
          household's shop day, and until this existed the most recent plan sat
          here forever with nothing marking it finished. It stays readable —
          somebody may be checking what they bought, or cooking the last night
          of it — and the prompt sits above rather than instead of it. */}
      {staleWeek && (
        <div className="border-line mb-5 rounded-xl border p-3 print:hidden">
          <p className="text-sm font-semibold">This is last week&rsquo;s plan.</p>
          <p className="text-muted mt-1 text-xs">
            Your shop day has come round again. Everything below still works — the list, the
            recipes, the ratings — and planning a new week replaces it.
          </p>
        </div>
      )}

      <div role="tablist" className="border-line mb-6 flex rounded-lg border p-1 print:hidden">
        {(["plan", "list"] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className="flex-1 rounded-md py-2 text-sm font-medium capitalize"
            style={
              tab === t
                ? { background: "var(--color-plate-b)", color: "var(--color-ground)" }
                : { color: "var(--color-muted)" }
            }
          >
            {t === "plan" ? "The plan" : "The list"}
          </button>
        ))}
      </div>

      <div className={tab === "plan" ? "" : "printable"}>
        {tab === "plan" ? <Days plan={plan} /> : <List plan={plan} />}
      </div>
    </Shell>
  );
}

function Days({ plan }: { plan: Plan }) {
  // The eaters, for the after-dinner thumbs. One call the page already makes.
  const { data: hh } = useQuery({ queryKey: ["household"], queryFn: twoPlates.household });
  const eaters = hh?.household?.eaters ?? [];
  // Derived from the plan, never assumed: a plan built when the household ran
  // three days must keep rendering as three even after they switch to seven.
  const days = [...new Set(plan.slots.map((s) => s.dayIndex))].sort((a, b) => a - b);
  return (
    <div className="flex flex-col gap-6">
      {plan.notes.length > 0 && (
        <ul className="border-line flex flex-col gap-1 rounded-lg border p-4">
          {plan.notes.map((n) => (
            <li key={n} className="text-muted text-xs">{n}</li>
          ))}
        </ul>
      )}

      {days.map((day) => {
        const slots = plan.slots.filter((s) => s.dayIndex === day);
        if (slots.length === 0) return null;
        return (
          <section key={day}>
            <h2 className="text-muted text-xs uppercase tracking-wide">{DAY_LABELS[day] ?? `Day ${day + 1}`}</h2>
            <div className="mt-2 flex flex-col gap-2">
              {SLOT_ORDER.map((name) => {
                const slot = slots.find((s) => s.slot === name);
                if (!slot) return null;
                return (
                  <SlotRow
                    key={name}
                    slot={slot}
                    eaters={eaters}
                    planId={plan.id}
                    usedMealIds={plan.slots.map((s) => s.mealId).filter((id): id is number => !!id)}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SlotRow({
  slot,
  eaters,
  planId,
  usedMealIds,
}: {
  slot: PlanSlot;
  eaters: Eater[];
  planId: number;
  usedMealIds: number[];
}) {
  const unsolved = slot.source === "unsolved";
  return (
    <div
      className="bg-surface border-line rounded-xl border p-4"
      style={unsolved ? { borderColor: "var(--color-warn)" } : undefined}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-muted text-xs capitalize">{slot.slot}</p>
        {slot.source === "leftover" && (
          <p className="text-muted text-[11px]">last night's pan</p>
        )}
        {slot.startTime && (
          <p className="text-plate-a text-[11px]">{slot.startTime}</p>
        )}
      </div>

      {unsolved ? (
        // Saying "unsolved" beats filling the row with something nobody eats.
        // A plan you cannot believe is a plan you stop opening.
        <p className="text-warn mt-1 text-sm">
          Nothing here yet — {slot.note ?? "no leftovers, so this one needs a decision"}
        </p>
      ) : (
        <>
          <p className="mt-1 font-medium">{slot.mealName}</p>
          {/* ⚠️ Any slot that knows its meal, not just a chosen dinner. Breakfast
              repeats on days two and three are the SAME meal, and somebody
              standing in the kitchen on Wednesday needs the method as much as
              they did on Monday (#1342). */}
          {slot.mealId && (
            <Link
              to={`/cook/${slot.mealId}`}
              className="text-plate-b mt-1 inline-block text-sm underline underline-offset-4"
            >
              How to cook it
            </Link>
          )}
          {slot.sides.length > 0 && (
            <p className="text-muted mt-0.5 text-sm">with {slot.sides.join(", ")}</p>
          )}
          {slot.note && <p className="text-muted mt-1 text-xs">{slot.note}</p>}

          {/* ⚠️ Only on a dinner that was actually cooked from a library meal.
              Asking "how was it" about a leftover lunch is asking twice. */}
          {slot.source === "chosen" && slot.id && slot.mealName && eaters.length > 0 && (
            <HowWasIt slotId={slot.id} mealName={slot.mealName} eaters={eaters} />
          )}

          {/* ⚠️ A DIFFERENT question from HowWasIt, and deliberately after it:
              that one asks each person whether they liked dinner, this asks the
              household whether the meal did what the library promised. Only on a
              chosen dinner — asking whether a leftover lunch "worked as two
              plates" is asking about the wrong thing. */}
          {slot.source === "chosen" && slot.mealId && eaters.length > 1 && (
            <DidItWork mealId={slot.mealId} />
          )}

          {/* Only a chosen dinner can be swapped — a leftover lunch is not a
              meal you get to change, it is last night's pan. */}
          {slot.source === "chosen" && slot.id && (
            <SwapMeal planId={planId} slotId={slot.id} usedMealIds={usedMealIds} />
          )}
        </>
      )}
    </div>
  );
}

function List({ plan }: { plan: Plan }) {
  // Brands this household has already told us about (#1300).
  const { data: prefs } = useQuery({
    queryKey: ["item-preferences"],
    queryFn: twoPlates.itemPreferences,
  });
  const brands = useMemo(
    () => new Map((prefs?.preferences ?? []).map((p) => [p.itemName, p])),
    [prefs],
  );

  // Most of a three-day list is doubled, because two of the three dinners make
  // the next day's lunch. Repeating that on every line turns it into wallpaper
  // and pushes the thing you are actually looking for off the end of the row,
  // so it is said ONCE here and marked per line only where it applies.
  const doubled = plan.items.filter((i) => i.qtyNote).length;

  const byAisle = useMemo(() => {
    const groups = new Map<string, PlanItem[]>();
    for (const item of plan.items) {
      const list = groups.get(item.aisle) ?? [];
      list.push(item);
      groups.set(item.aisle, list);
    }
    // The walk through a shop, not the alphabet. An aisle the library invents
    // that we have not ordered goes last rather than disappearing.
    return [...groups.entries()].sort(([a], [b]) => {
      const ia = AISLE_ORDER.indexOf(a);
      const ib = AISLE_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [plan.items]);

  return (
    <div className="flex flex-col gap-6">
      <ShareList planId={plan.id} />

      <Link
        to={`/plans/${plan.id}/audit`}
        className="border-line tap flex items-center justify-center rounded-lg border text-sm font-medium"
      >
        Check what you already have
      </Link>

      {doubled > 0 && (
        <p className="text-muted border-line rounded-lg border px-4 py-3 text-xs">
          {doubled} of these are doubled — two of the three dinners make the next
          day's lunch. They are marked <span className="text-plate-a">×2</span>.
        </p>
      )}

      {byAisle.map(([aisle, items]) => (
        <section key={aisle}>
          <h2 className="text-muted text-xs uppercase tracking-wide">
            {AISLE_LABEL[aisle] ?? aisle}
          </h2>
          <ul className="mt-2 flex flex-col">
            {items.map((item) => (
              <ListRow key={item.id} planId={plan.id} item={item} brand={brands.get(item.itemName)} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ListRow({
  planId,
  item,
  brand,
}: {
  planId: number;
  item: PlanItem;
  brand?: ItemPreference;
}) {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(item.checked);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(false);

  const tick = useMutation({
    mutationFn: (next: boolean) => twoPlates.tickItem(planId, item.id, { checked: next }),
    onError: (_e, next) => {
      // Roll back honestly. A tick that silently did not save is a thing you
      // discover at the till.
      setChecked(!next);
      setFailed(true);
    },
    onSuccess: () => {
      setFailed(false);
      queryClient.invalidateQueries({ queryKey: ["plan"] });
    },
  });

  function toggle() {
    const next = !checked;
    setChecked(next); // optimistic: bad signal in a shop is the normal case
    tick.mutate(next);
  }

  const struck = checked || item.haveAlready;

  return (
    <li>
      <button
        onClick={toggle}
        aria-pressed={checked}
        className="tap border-line flex w-full items-center gap-3 border-b px-1 text-left"
      >
        <span
          aria-hidden
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs"
          style={
            struck
              ? { background: "var(--color-plate-b)", borderColor: "var(--color-plate-b)", color: "var(--color-ground)" }
              : { borderColor: "var(--color-line)" }
          }
        >
          {struck ? "✓" : ""}
        </span>
        <span className="min-w-0 flex-1 py-2">
          <span className={struck ? "text-muted line-through" : ""}>
            {item.itemName}
          </span>
          <span className="text-muted block text-[11px]">
            {/* The quantity sits on the reason line, not beside the name: a
                line already reading "Bell peppers ×2" must not then also read
                "×2" in the same breath. */}
            {item.qtyNote && <span className="text-plate-a">×2 · </span>}
            {brand?.brand && <span className="text-plate-a">{brand.brand} · </span>}
            {item.forMeals.join(", ")}
            {item.haveAlready && " · already have it"}
            {item.style === "pack" && " · the packet"}
          </span>
          {failed && (
            <span className="text-warn block text-[11px]">did not save — tap again</span>
          )}
        </span>
      </button>

      {/* ⚠️ A brand is a HINT, never a constraint. The line still says what the
          item is; the brand rides along, and if the shop does not have it
          nothing broke. */}
      {editing ? (
        <BrandForm item={item} brand={brand} onDone={() => setEditing(false)} />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="text-muted mb-2 ml-8 text-[11px] underline underline-offset-4"
        >
          {brand?.brand ? "Change the brand" : "Remember a brand"}
        </button>
      )}
    </li>
  );
}

/**
 * What was actually bought, so next time the line says it (#1300).
 *
 * Collected here rather than in a setup wizard, because this is the moment
 * somebody knows the answer: standing in the aisle holding the tin.
 */
function BrandForm({
  item,
  brand,
  onDone,
}: {
  item: PlanItem;
  brand?: ItemPreference;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(brand?.brand ?? "");

  const save = useMutation({
    mutationFn: () =>
      twoPlates.saveItemPreference({
        itemName: item.itemName,
        brand: value.trim() || null,
        starred: value.trim().length > 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["item-preferences"] });
      onDone();
    },
  });

  return (
    <div className="border-line mb-2 ml-8 rounded-lg border p-3">
      <label htmlFor={`brand-${item.id}`} className="text-muted text-[11px] uppercase tracking-wide">
        Which one did you buy?
      </label>
      <input
        id={`brand-${item.id}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Store brand, the good tortillas, whatever…"
        className="bg-surface border-line mt-2 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-plate-b"
      />
      <p className="text-muted mt-1.5 text-[11px]">
        A hint, not a rule. The list still says {item.itemName}.
      </p>
      <div className="mt-2 flex gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="border-line rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Remember it"}
        </button>
        <button onClick={onDone} className="text-muted text-xs underline underline-offset-4">
          Cancel
        </button>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  // The app chrome owns the page padding now (#1323); this is only a grouping.
  return <>{children}</>;
}
